// External libraries
import React from 'react';
import { useStoreState } from '@/store';
// @ts-expect-error untyped-import
import { FieldError } from '@checkout/react-components';

// Internal modules
import { getContent } from '@/lib';
import { features } from '@/lib/features';
import { clickHandler } from './handlers';
import { SectionSubHeading } from '../../../Headings';
import { ButtonLink, errorStyleOverrides } from './StyledComponents';
import { Checkbox } from '@/components/Common';
import type { InputOnChange } from '@/types';
import {
  atomicsDomain,
  atomicsFlows,
  atomicsTasks,
  atomicsViewNames,
} from '@/lib/atomics';

const sepaDirectDebitButton = () => ({
  sepaDirectDebit: (
    <ButtonLink
      data-testid="sepa-direct-debit-button-link"
      onClick={clickHandler}
      data-atomic-wait-domain={atomicsDomain}
      data-atomic-wait-flow={atomicsFlows.guestAndSignup}
      data-atomic-wait-intent={`click_${atomicsTasks.viewSepaInterstitial}`}
      data-atomic-wait-task={atomicsTasks.viewSepaInterstitial}
      data-atomic-wait-viewname={atomicsViewNames.reviewYourPayment}
    >
      {getContent('sepa.directDebit')}
    </ButtonLink>
  ),
});

type SepaMandateProps = {
  changeHandler: InputOnChange;
};

const SepaMandate = ({ changeHandler }: SepaMandateProps): JSX.Element => {
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const sepaMandate = useStoreState(({ form }) => form.sepaMandate);
  const accountType = useStoreState(({ form }) => form.onboardOption.value);
  const hasError = Boolean(sepaMandate?.displayError);
  const hideSepaTermsCheckbox = features.someActive(
    'isStreamlineOptionalExperience',
    'isStreamlineForcedExperience',
  );
  const isGuestFlow = accountType !== 'signup';
  const hasGuestXOSepaUpdate = features.isActive('hasGuestXOSepaUpdate');
  const legalAgreement =
    isGuestFlow && hasGuestXOSepaUpdate
      ? getContent('sepa.legalAgreementToggleOff', sepaDirectDebitButton())
      : getContent('sepa.legalAgreement', sepaDirectDebitButton());

  // Hide sepa checkbox if streamline exp is active and country is DE
  if (hideSepaTermsCheckbox) {
    return (
      <SectionSubHeading fontSize="13px" data-testid="streamline-sepa-terms">
        {legalAgreement}
      </SectionSubHeading>
    );
  }

  return (
    <>
      <Checkbox
        aria-describedby={hasError ? 'sepaMandate-error' : null}
        aria-invalid={hasError}
        changeHandler={changeHandler}
        checked={sepaMandate.value}
        disabled={isFormSubmitting}
        id="sepaMandate"
        label={legalAgreement}
        name="sepaMandate"
        isTermsCheckbox
        required={true}
        {...sepaMandate}
      />
      {hasError && (
        <FieldError
          id="sepa-mandate-error"
          styleOverrides={errorStyleOverrides}
        >
          {getContent('fieldErrors.signupTermsMissing')}
        </FieldError>
      )}
    </>
  );
};

export { SepaMandate };
